// frontend/src/askos/mocktest/MockTestResultPage.js
//
// Score + per-question review after a submitted mock attempt, grouped by
// full-question/choice-group to mirror the runner and the real exam
// structure. Descriptive sub-parts show the student's written answer next
// to the model answer here (withheld until now) with a self-score input —
// self-assessment happens here, not during the timed test.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMockAttemptResult, updateSelfScores } from '../../api/mockTestApi';

const SubPartReview = ({ sp, selfScoreDraft, onSelfScoreChange }) => (
    <div style={{ marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--line)' }}>
        <div className="d-flex justify-content-between">
            <strong style={{ fontSize: 13 }}>({sp.sub_part}) {sp.question_text}</strong>
            <span style={{ fontWeight: 700, color: sp.score > 0 ? '#1a7f37' : sp.score < 0 ? '#c0392b' : 'var(--muted)' }}>
                {sp.score} / {sp.marks}
            </span>
        </div>

        {sp.options ? (
            <p className="empty-note" style={{ marginTop: 6 }}>
                Your answer: <strong>{sp.selected_answer || '(not attempted)'}</strong> · Correct answer: <strong>{sp.answer_key}</strong>
            </p>
        ) : (
            <>
                <p className="empty-note" style={{ marginTop: 6 }}><strong>Your answer:</strong> {sp.selected_answer || '(not attempted)'}</p>
                {sp.answer_key && <p className="empty-note"><strong>Model Answer:</strong> {sp.answer_key}</p>}
                {sp.attempted && (
                    <div className="d-flex align-items-center gap-2 mt-1">
                        <span className="empty-note">Self-score (0–{sp.marks}):</span>
                        <input
                            type="number"
                            min={0}
                            max={sp.marks}
                            className="form-control form-control-sm"
                            style={{ width: 80 }}
                            value={selfScoreDraft ?? sp.self_score ?? ''}
                            onChange={e => onSelfScoreChange(sp.id, e.target.value)}
                        />
                    </div>
                )}
            </>
        )}
    </div>
);

const MockTestResultPage = ({ slug }) => {
    const { attemptId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        getMockAttemptResult(slug, attemptId)
            .then(res => { setData(res); setDrafts({}); })
            .catch(err => setError(err.response?.data?.detail || 'Could not load result.'));
    }, [slug, attemptId]);

    useEffect(() => { load(); }, [load]);

    const hasDescriptive = (subParts) => subParts.some(sp => !sp.options && sp.attempted);
    const anyDraftsPending = Object.keys(drafts).length > 0;

    const handleSaveScores = async () => {
        setSaving(true);
        try {
            await updateSelfScores(slug, attemptId, drafts);
            load();
        } finally {
            setSaving(false);
        }
    };

    if (error) return <div className="subpage"><p className="empty-note">{error}</p></div>;
    if (!data) return <div className="subpage"><p className="empty-note">Loading…</p></div>;

    return (
        <div className="subpage">
            <div className="panel-heading" style={{ marginBottom: 16 }}>
                <h2>{data.test_title} — Result</h2>
                <button type="button" className="secondary-btn" onClick={() => navigate('/samkalp-ias/mock-tests')}>← Back to Mock Tests</button>
            </div>

            <article className="panel" style={{ marginBottom: 20, textAlign: 'center' }}>
                <p className="empty-note">Your Score</p>
                <h2 style={{ margin: 0 }}>{data.score} / {data.total_marks}</h2>
            </article>

            {data.compulsory.map(c => (
                <article key={c.question_number} className="panel" style={{ marginBottom: 14 }}>
                    <div className="panel-heading"><h3>Question {c.question_number}</h3></div>
                    {c.sub_parts.map(sp => (
                        <SubPartReview
                            key={sp.id}
                            sp={sp}
                            selfScoreDraft={drafts[sp.id]}
                            onSelfScoreChange={(id, val) => setDrafts(d => ({ ...d, [id]: val }))}
                        />
                    ))}
                </article>
            ))}

            {data.choice_groups.map(g => (
                <div key={g.group_label} style={{ marginBottom: 20 }}>
                    <p className="empty-note" style={{ fontWeight: 700 }}>{g.group_label} — attempt any {g.attempt_count} of {g.of_count}</p>
                    {g.questions.map(q => (
                        <article key={q.question_number} className="panel" style={{ marginBottom: 14, opacity: q.attempted ? 1 : 0.6 }}>
                            <div className="panel-heading">
                                <h3>Question {q.question_number}</h3>
                                {q.attempted ? (
                                    <span style={{ fontWeight: 700, color: q.counted ? '#1a7f37' : 'var(--muted)' }}>
                                        {q.counted ? '✓ Counted' : 'Not counted (best 2 chosen)'} · {q.total_score}
                                    </span>
                                ) : (
                                    <span className="empty-note">Not attempted</span>
                                )}
                            </div>
                            {q.sub_parts.map(sp => (
                                <SubPartReview
                                    key={sp.id}
                                    sp={sp}
                                    selfScoreDraft={drafts[sp.id]}
                                    onSelfScoreChange={(id, val) => setDrafts(d => ({ ...d, [id]: val }))}
                                />
                            ))}
                        </article>
                    ))}
                </div>
            ))}

            {(hasDescriptive([...data.compulsory.flatMap(c => c.sub_parts), ...data.choice_groups.flatMap(g => g.questions.flatMap(q => q.sub_parts))])) && (
                <button type="button" className="primary-btn" onClick={handleSaveScores} disabled={saving || !anyDraftsPending}>
                    {saving ? 'Saving…' : 'Save Self-Scores'}
                </button>
            )}
        </div>
    );
};

export default MockTestResultPage;
